import React, { useState, useMemo } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select, Tag,
  Alert, Segmented, Drawer, DatePicker, message,
} from 'antd';
import { SearchOutlined, ReloadOutlined, ExportOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// ────────────────────────────────────────────
//  常量
// ────────────────────────────────────────────

// 生产测试项（与固件「生产测试项配置」一致）
const TEST_ITEMS = [
  '图像测试', 'WIFI信号', '电池测试', 'TF卡读写', '指示灯测试', '麦克风测试',
  '喇叭测试', 'PTZ测试', 'PIR检测', '蓝牙测试', '复位测试', '呼叫测试', '电源插口测试',
];

const passAll = () => TEST_ITEMS.map((name) => ({ name, result: 'pass' }));
const failWith = (failName) => TEST_ITEMS.map((name) => ({ name, result: name === failName ? 'fail' : 'pass' }));

const PRODUCT_OPTIONS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
  { value: 'PROD_D1', label: '智能门铃Pro' },
];

const ENTERPRISE_OPTIONS = [
  { value: 'ENT001', label: '深圳智创科技有限公司' },
  { value: 'ENT002', label: '杭州万物互联有限公司' },
];

// 烧号状态：produced=烧号成功，failed=烧号失败，pending=未烧号（只测试）
// 计数口径：按 License 台数、以最终结果为准（失败后重试成功只记成功一次）
const BURN_STATUS_MAP = {
  produced: { text: '烧号成功', color: 'success' },
  failed: { text: '烧号失败', color: 'error' },
  pending: { text: '未烧号', color: 'warning' },
};

// 产测账号所属分组
const ACCOUNT_GROUP = {
  'test-worker-zhang': '华东生产部',
  'test-worker-li': '华东生产部',
  'test-worker-wang': '华南外协厂',
  'test-worker-zhao': '默认分组',
};

// ────────────────────────────────────────────
//  Mock 数据 — TODO: 联调时替换为接口数据
//  粒度：产测账号 × 自然日 × License（明细），列表由明细聚合
//  tests 为空表示该 License 未上报测试记录
// ────────────────────────────────────────────

const MOCK_LICENSE_DETAILS = [
  // 张师傅 · 深圳智创
  { key: 'z1', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-001', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-14 09:12:03', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z2', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-002', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-14 10:41:22', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z3', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-003', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-14 11:08:45', tests: failWith('电池测试'), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z4', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-004', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-14 14:33:10', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z8', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-008', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-14 15:06:52', tests: passAll(), burnStatus: 'pending', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z9', account: 'test-worker-zhang', date: '2026-07-14', license: 'test-license-009', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '', tests: [], burnStatus: 'failed', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z5', account: 'test-worker-zhang', date: '2026-07-13', license: 'test-license-005', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-13 09:05:00', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z6', account: 'test-worker-zhang', date: '2026-07-13', license: 'test-license-006', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-13 10:20:31', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'z7', account: 'test-worker-zhang', date: '2026-07-13', license: 'test-license-007', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderId: 'WO202607001', testTime: '2026-07-13 15:47:19', tests: failWith('WIFI信号'), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },

  // 李师傅 · 深圳智创
  { key: 'l1', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-101', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-14 08:55:12', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l2', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-102', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-14 09:31:40', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l3', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-103', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-14 10:12:08', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l4', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-104', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-14 13:26:55', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l5', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-105', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-14 16:02:37', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l6', account: 'test-worker-li', date: '2026-07-13', license: 'test-license-106', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-13 11:18:44', tests: passAll(), enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l7', account: 'test-worker-li', date: '2026-07-13', license: 'test-license-107', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '2026-07-13 14:50:09', tests: passAll(), burnStatus: 'pending', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: 'l8', account: 'test-worker-li', date: '2026-07-14', license: 'test-license-108', productName: '云台机P1', productId: 'PROD_P1', orderId: 'WO202607003', testTime: '', tests: [], burnStatus: 'failed', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },

  // 王师傅 · 杭州万物
  { key: 'w1', account: 'test-worker-wang', date: '2026-07-14', license: 'test-license-201', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '2026-07-14 09:44:21', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'w2', account: 'test-worker-wang', date: '2026-07-14', license: 'test-license-202', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '2026-07-14 11:02:53', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'w3', account: 'test-worker-wang', date: '2026-07-14', license: 'test-license-203', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '2026-07-14 15:31:18', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'w5', account: 'test-worker-wang', date: '2026-07-14', license: 'test-license-205', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '2026-07-14 16:08:41', tests: failWith('蓝牙测试'), burnStatus: 'pending', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'w6', account: 'test-worker-wang', date: '2026-07-14', license: 'test-license-206', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '', tests: [], burnStatus: 'failed', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'w4', account: 'test-worker-wang', date: '2026-07-13', license: 'test-license-204', productName: '烟雾报警器F1', productId: 'PROD_F1', orderId: 'WO202607005', testTime: '2026-07-13 10:27:46', tests: failWith('喇叭测试'), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },

  // 赵师傅 · 杭州万物
  { key: 'o1', account: 'test-worker-zhao', date: '2026-07-14', license: 'test-license-301', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-14 08:36:02', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o2', account: 'test-worker-zhao', date: '2026-07-14', license: 'test-license-302', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-14 09:58:30', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o7', account: 'test-worker-zhao', date: '2026-07-14', license: 'test-license-307', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-14 10:22:17', tests: passAll(), burnStatus: 'pending', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o3', account: 'test-worker-zhao', date: '2026-07-12', license: 'test-license-303', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-12 10:14:11', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o4', account: 'test-worker-zhao', date: '2026-07-12', license: 'test-license-304', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-12 11:45:27', tests: passAll(), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o5', account: 'test-worker-zhao', date: '2026-07-12', license: 'test-license-305', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-12 14:19:58', tests: failWith('PIR检测'), enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: 'o6', account: 'test-worker-zhao', date: '2026-07-12', license: 'test-license-306', productName: '智能门铃Pro', productId: 'PROD_D1', orderId: 'WO202607006', testTime: '2026-07-12 16:37:41', tests: [], enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
].map((r) => ({ burnStatus: 'produced', ...r }));

// ────────────────────────────────────────────
//  测试结果标签
// ────────────────────────────────────────────

const renderTestResults = (tests) => {
  if (!tests || tests.length === 0) {
    return <Text type="secondary">--</Text>;
  }
  return (
    <Space size={[4, 4]} wrap>
      {tests.map((t) => (
        <Tag key={t.name} color={t.result === 'pass' ? 'success' : 'error'} style={{ border: 'none', marginInlineEnd: 0 }}>
          {t.name}
        </Tag>
      ))}
    </Space>
  );
};

// ────────────────────────────────────────────
//  列表列
// ────────────────────────────────────────────

const getColumns = (showEnterprise, onOpenDrawer, getPct) => {
  const cols = [
    {
      title: '产测账号', dataIndex: 'account', width: 180, fixed: 'left',
      render: (v) => (
        <div>
          <div>{v}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{ACCOUNT_GROUP[v] || '-'}</Text>
        </div>
      ),
    },
    {
      title: '日期', dataIndex: 'date', width: 130,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.date.localeCompare(b.date),
      showSorterTooltip: { title: '点击排序' },
    },
    { title: '烧号成功数', dataIndex: 'burnSuccess', width: 120 },
    { title: '烧号失败数', dataIndex: 'burnFailed', width: 120 },
    { title: '测试总数', dataIndex: 'tested', width: 120 },
    { title: '测试通过数', dataIndex: 'passed', width: 120 },
    { title: '测试失败数', dataIndex: 'failed', width: 120 },
    {
      title: '测试通过率', key: 'passRate', width: 140,
      render: (_, r) => (
        <Text style={{ fontWeight: 500 }}>{getPct(r.passed, r.failed)}</Text>
      ),
    },
  ];
  if (showEnterprise) {
    cols.push({
      title: '所属企业', key: 'enterprise', width: 200,
      render: (_, r) => (
        <div>
          <div>{r.enterpriseName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.enterpriseId}</Text>
        </div>
      ),
    });
  }
  cols.push({
    title: '操作', key: 'action', width: 100, fixed: 'right',
    render: (_, r) => (
      <Button type="link" size="small" onClick={() => onOpenDrawer(r)}>查看详情</Button>
    ),
  });
  return cols;
};

// ────────────────────────────────────────────
//  明细抽屉列
// ────────────────────────────────────────────

const drawerColumns = [
  {
    title: 'License', dataIndex: 'license', width: 200,
    render: (v) => (
      <Button
        type="link"
        size="small"
        style={{ fontFamily: 'monospace', padding: 0, height: 'auto' }}
        onClick={() => window.open(`/device/license-query?license=${v}`, '_blank')}
      >
        {v}
      </Button>
    ),
  },
  {
    title: '产品信息', key: 'product', width: 160,
    render: (_, r) => (
      <div>
        <div>{r.productName}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text>
      </div>
    ),
  },
  { title: '工单ID', dataIndex: 'orderId', width: 150, render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
  {
    title: '烧号状态', dataIndex: 'burnStatus', width: 150,
    render: (v) => {
      const b = BURN_STATUS_MAP[v] || { text: '-', color: 'default' };
      return <Tag color={b.color} style={{ border: 'none' }}>{b.text}</Tag>;
    },
  },
  { title: '测试结果', key: 'tests', width: 480, render: (_, r) => renderTestResults(r.tests) },
  { title: '测试时间', dataIndex: 'testTime', width: 180, render: (v) => v || <Text type="secondary">-</Text> },
];

// ══════════════════════════════════════════════
//  主组件：ProdTestStatistics
// ══════════════════════════════════════════════

const ProdTestStatistics = () => {
  const [form] = Form.useForm();

  // Mock 角色视角切换（真实业务由登录角色决定）
  const [mockRole, setMockRole] = useState('platform');

  // 已应用的查询条件（默认无日期范围，查询全部）
  const [applied, setApplied] = useState({});
  const [hasQueried, setHasQueried] = useState(false);

  // 明细抽屉
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);

  const isPlatform = mockRole === 'platform';

  // ── 测试通过率（加权：通过 ÷（通过+失败），无测试记录返回 --）──
  const getPct = (passed, failed) => {
    const tested = passed + failed;
    if (tested === 0) return '--';
    return `${((passed / tested) * 100).toFixed(1)}%`;
  };

  // ── 角色过滤后的明细基础数据 ──
  const baseDetails = useMemo(() => {
    if (mockRole === 'enterprise') {
      return MOCK_LICENSE_DETAILS.filter((r) => r.enterpriseId === 'ENT001');
    }
    return MOCK_LICENSE_DETAILS;
  }, [mockRole]);

  // ── 按已应用条件过滤明细 ──
  const filteredDetails = useMemo(() => {
    let data = [...baseDetails];
    const { dateRange, account, productKeyword, productSearchType, orderId, enterpriseKeyword, enterpriseSearchType } = applied;

    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      data = data.filter((r) => r.date >= start && r.date <= end);
    }
    if (account) {
      data = data.filter((r) => r.account.includes(account));
    }
    if (productKeyword) {
      if ((productSearchType || 'name') === 'name') {
        data = data.filter((r) => r.productId === productKeyword);
      } else {
        data = data.filter((r) => r.productId.includes(productKeyword));
      }
    }
    if (orderId) {
      data = data.filter((r) => r.orderId.includes(orderId));
    }
    if (isPlatform && enterpriseKeyword) {
      if ((enterpriseSearchType || 'name') === 'name') {
        data = data.filter((r) => r.enterpriseId === enterpriseKeyword);
      } else {
        data = data.filter((r) => r.enterpriseId.includes(enterpriseKeyword));
      }
    }
    return data;
  }, [baseDetails, applied, isPlatform]);

  // ── 聚合：产测账号 × 日期 ──
  //  烧号成功数 = 最终烧号成功 License 台数（重试成功只记成功一次）
  //  烧号失败数 = 最终烧号失败 License 台数
  //  测试总数 = 有测试记录的 License 数（含「未烧号」）
  const rows = useMemo(() => {
    const groups = {};
    filteredDetails.forEach((d) => {
      const gk = `${d.account}_${d.date}`;
      if (!groups[gk]) {
        groups[gk] = {
          key: gk, account: d.account, date: d.date,
          burnSuccess: 0, burnFailed: 0, tested: 0, passed: 0, failed: 0,
          enterpriseName: d.enterpriseName, enterpriseId: d.enterpriseId,
        };
      }
      const g = groups[gk];
      if (d.burnStatus === 'produced') g.burnSuccess += 1;
      else if (d.burnStatus === 'failed') g.burnFailed += 1;
      if (d.tests.length > 0) {
        g.tested += 1;
        if (d.tests.every((t) => t.result === 'pass')) g.passed += 1;
        else g.failed += 1;
      }
    });
    return Object.values(groups).sort(
      (a, b) => b.date.localeCompare(a.date) || a.account.localeCompare(b.account)
    );
  }, [filteredDetails]);

  // ── 查询 ──
  const handleQuery = () => {
    setApplied(form.getFieldsValue());
    setHasQueried(true);
  };

  // ── 重置 ──
  const handleReset = () => {
    form.resetFields();
    setApplied({});
    setHasQueried(false);
  };

  // ── 角色切换 ──
  const handleRoleChange = (role) => {
    setMockRole(role);
    form.resetFields();
    setApplied({});
    setHasQueried(false);
  };

  // ── 导出 ──
  const handleExport = () => {
    message.success('产测统计数据导出成功，请查看下载列表');
  };

  const handleDrawerExport = () => {
    message.success('License 明细导出成功，请查看下载列表');
  };

  // ── 打开明细抽屉 ──
  const openDrawer = (row) => {
    setDrawerRow(row);
    setDrawerVisible(true);
  };

  const drawerRecords = useMemo(() => {
    if (!drawerRow) return [];
    return filteredDetails
      .filter((d) => d.account === drawerRow.account && d.date === drawerRow.date)
      .sort((a, b) => a.testTime.localeCompare(b.testTime));
  }, [filteredDetails, drawerRow]);

  const columns = getColumns(isPlatform, openDrawer, getPct);
  const emptyText = hasQueried ? '暂无符合条件的数据' : '暂无数据';

  return (
    <div style={{ padding: 24 }}>
      {/* 标题行 + Mock 角色视角切换 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>产测统计</Title>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
          <Segmented
            value={mockRole}
            onChange={handleRoleChange}
            options={[
              { label: '平台视角', value: 'platform' },
              { label: '企业视角', value: 'enterprise' },
            ]}
          />
        </Space>
      </div>

      {/* 信息提示 */}
      <Alert
        type="info"
        showIcon
        closable
        message="当前仅展示企业在平台产测工具上产生的产测生产数据；未使用平台产测工具的工单不产生统计数据"
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* 筛选区 */}
      <Card
        bordered={false}
        style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
        styles={{ body: { padding: '16px 24px' } }}
      >
        <Form form={form} layout="inline" style={{ rowGap: 12, flexWrap: 'wrap' }}>
          <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
            <RangePicker />
          </Form.Item>

          <Form.Item name="account" style={{ marginBottom: 0 }}>
            <Input placeholder="产测账号" allowClear style={{ width: 180 }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, flex: '1 1 320px', minWidth: 320 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="productSearchType" noStyle initialValue="name">
                <Select style={{ width: 110 }} onChange={() => form.setFieldsValue({ productKeyword: undefined })}>
                  <Select.Option value="name">产品名称</Select.Option>
                  <Select.Option value="id">产品ID</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(p, c) => p.productSearchType !== c.productSearchType}>
                {({ getFieldValue }) => {
                  const type = getFieldValue('productSearchType') || 'name';
                  return type === 'name' ? (
                    <Form.Item name="productKeyword" noStyle>
                      <Select
                        placeholder="请选择产品"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        options={PRODUCT_OPTIONS}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item name="productKeyword" noStyle>
                      <Input placeholder="请输入产品ID" allowClear style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item name="orderId" style={{ marginBottom: 0 }}>
            <Input placeholder="工单ID" allowClear style={{ width: 180 }} />
          </Form.Item>

          {isPlatform && (
            <Form.Item style={{ marginBottom: 0, flex: '1 1 320px', minWidth: 320 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="enterpriseSearchType" noStyle initialValue="name">
                  <Select style={{ width: 110 }} onChange={() => form.setFieldsValue({ enterpriseKeyword: undefined })}>
                    <Select.Option value="name">企业名称</Select.Option>
                    <Select.Option value="id">企业ID</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.enterpriseSearchType !== c.enterpriseSearchType}>
                  {({ getFieldValue }) => {
                    const type = getFieldValue('enterpriseSearchType') || 'name';
                    return type === 'name' ? (
                      <Form.Item name="enterpriseKeyword" noStyle>
                        <Select
                          placeholder="请选择企业"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                          options={ENTERPRISE_OPTIONS}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item name="enterpriseKeyword" noStyle>
                        <Input placeholder="请输入企业ID" allowClear style={{ width: '100%' }} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 表格区 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="key"
          scroll={{ x: isPlatform ? 1500 : 1320 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条记录`,
          }}
          locale={{ emptyText }}
        />
      </Card>

      {/* License 明细抽屉 */}
      <Drawer
        title={drawerRow ? `License 明细 — ${drawerRow.account} · ${drawerRow.date}` : 'License 明细'}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width="80%"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>标签颜色：绿色=通过，红色=失败；「--」表示未上报测试记录；「未烧号」表示该 License 未烧号但有测试记录，计入测试总数，不计入烧号成功/失败数；烧号失败按最终结果计一台，重试成功不重复计数</Text>
          <Button icon={<ExportOutlined />} onClick={handleDrawerExport}>导出</Button>
        </div>
        <Table
          columns={drawerColumns}
          dataSource={drawerRecords}
          rowKey="key"
          scroll={{ x: 1300 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条记录`,
          }}
          locale={{ emptyText: '暂无明细数据' }}
        />
      </Drawer>
    </div>
  );
};

export default ProdTestStatistics;
